import { Link } from "react-router-dom";
import { ArrowUpRight, ImageIcon, FlaskConical, Clapperboard } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal, Eyebrow } from "@/components/Reveal";

const UPCOMING = [
    {
        icon: FlaskConical,
        tag: "Next",
        title: "New AI & Digital Products",
        text: "Additional proprietary applications and web platforms across artificial intelligence and emerging technology categories.",
        status: "Under Development / Planned",
    },
    {
        icon: Clapperboard,
        tag: "Future",
        title: "AI Entertainment & Streaming",
        text: "An AI-focused digital entertainment platform where users could discover and experience AI-generated films and cinematic content through a subscription-based streaming experience.",
        status: "Long-Term Product Direction",
    },
];

export default function Products() {
    return (
        <div data-testid="products-page">
            <SEO
                title="Products | ANA International Private Limited"
                description="Explore the digital product portfolio of ANA International Private Limited — starting with Lyanka AI, an AI-powered image and video creation platform, with more AI products planned."
                path="/products"
            />

            <section className="relative pt-40 pb-20 overflow-hidden">
                <div className="absolute inset-0 grid-bg" />
                <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
                    <Reveal>
                        <Eyebrow>Product Portfolio</Eyebrow>
                        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl mt-6 leading-tight max-w-3xl">
                            Our <span className="gold-text">Products</span>
                        </h1>
                        <p className="text-[#a3a29b] text-base sm:text-lg leading-relaxed mt-8 max-w-3xl">
                            ANA INTERNATIONAL is being developed as a technology product company with a portfolio of
                            proprietary digital platforms — not a single application. New products will be added here
                            as the portfolio expands.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="pb-28">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    {/* Current product */}
                    <Reveal>
                        <div className="card-line rounded-3xl p-8 sm:p-12 grid lg:grid-cols-2 gap-10 items-center relative overflow-hidden" data-testid="product-lyanka-feature">
                            <div className="absolute top-[-30%] right-[-10%] w-[400px] h-[400px] glow-gold rounded-full pointer-events-none" />
                            <div className="relative z-10">
                                <div className="flex items-center gap-3">
                                    <span className="mono-label">Current</span>
                                    <span className="text-[10px] font-mono tracking-widest uppercase text-[#0a0a0b] bg-gradient-to-r from-[#e8c766] to-[#c9a227] rounded-full px-3 py-1">Live Product</span>
                                </div>
                                <h2 className="font-display font-bold text-3xl sm:text-4xl mt-5">LYANKA AI</h2>
                                <p className="text-[#a3a29b] text-base sm:text-lg leading-relaxed mt-5 max-w-lg">
                                    An AI-powered creative platform for generating images and videos. Designed for AI
                                    creators, filmmakers, video editors, content creators, designers, marketing
                                    professionals, digital artists, small businesses, creative agencies and storytellers.
                                </p>
                                <div className="flex flex-wrap gap-2 mt-7">
                                    {["AI Image Generation", "AI Video Generation", "Creative Visuals", "Subscription Access"].map((t) => (
                                        <span key={t} className="text-[11px] font-mono tracking-widest uppercase text-[#c9c8c2] border border-white/10 rounded-full px-3.5 py-1.5">
                                            {t}
                                        </span>
                                    ))}
                                </div>
                                <Link to="/lyanka-ai" className="btn-gold mt-9" data-testid="products-cta-lyanka">
                                    Explore Lyanka AI <ArrowUpRight size={16} />
                                </Link>
                            </div>
                            <div className="relative z-10 flex justify-center">
                                <div className="glass rounded-3xl p-8 floaty">
                                    <img src="/assets/lyanka-mark.png" alt="Lyanka AI platform" className="w-64 rounded-2xl" />
                                </div>
                            </div>
                        </div>
                    </Reveal>

                    {/* Upcoming */}
                    <div className="grid md:grid-cols-2 gap-6 mt-6" data-testid="upcoming-products">
                        {UPCOMING.map((p, i) => (
                            <Reveal key={p.title} delay={i * 0.1}>
                                <div className="card-line rounded-3xl p-10 h-full flex flex-col">
                                    <div className="flex items-center justify-between">
                                        <p.icon size={24} className="text-[#e8c766]" />
                                        <span className="mono-label !text-[10px]">{p.tag}</span>
                                    </div>
                                    <h3 className="font-display font-semibold text-xl mt-7">{p.title}</h3>
                                    <p className="text-sm text-[#a3a29b] leading-relaxed mt-4 flex-1">{p.text}</p>
                                    <p className="text-xs font-mono tracking-widest uppercase text-[#c9a227] mt-6">{p.status}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
